import SquareIcon from '@/assets/icons/square.svg?react'
import { memo, useEffect, useRef } from 'react'
import { formatTime } from './AudioRecordingTask'

type RecordingStateRecordingProps = {
  seconds: number
  analyser: AnalyserNode | null
  onStopRecording: () => void
}

// eslint-disable-next-line react/display-name
export const RecordingStateRecording = memo(({ seconds, analyser, onStopRecording }: RecordingStateRecordingProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!analyser || !canvas) return
    const ctx2d = canvas.getContext('2d')
    if (!ctx2d) return

    const dpr = window.devicePixelRatio || 1
    const barW = 3 * dpr
    const gap = 2 * dpr
    const STEP_MS = 40
    const GAIN = 2.6 // raw level (AGC off) is low; boost so normal speech fills the bars

    // pre-filled so the baseline spans full width and the waveform scrolls in from the right
    let bars: number[] = []
    const resize = () => {
      canvas.width = canvas.clientWidth * dpr
      canvas.height = canvas.clientHeight * dpr
      bars = new Array(Math.max(1, Math.floor(canvas.width / (barW + gap)))).fill(0)
    }
    resize()
    window.addEventListener('resize', resize)

    const data = new Uint8Array(analyser.fftSize)
    let acc = 0
    let lastStep = 0
    let raf = 0

    const draw = (now: number) => {
      analyser.getByteTimeDomainData(data)
      let peak = 0
      for (let i = 0; i < data.length; i++) {
        const v = Math.abs(data[i] - 128) / 128
        if (v > peak) peak = v
      }
      if (peak > acc) acc = peak
      if (now - lastStep >= STEP_MS) {
        bars.push(acc)
        bars.shift()
        acc = 0
        lastStep = now
      }

      ctx2d.clearRect(0, 0, canvas.width, canvas.height)
      ctx2d.fillStyle = '#E40027'
      const mid = canvas.height / 2
      for (let i = 0; i < bars.length; i++) {
        const h = Math.max(2 * dpr, Math.min(1, bars[i] * GAIN) * canvas.height)
        ctx2d.fillRect(i * (barW + gap), mid - h / 2, barW, h)
      }
      raf = requestAnimationFrame(draw)
    }
    raf = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [analyser])

  return (
    <div className="flex w-full flex-col items-center gap-2" style={{ animation: 'nectar-scale-in 0.3s ease-out' }}>
      <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
        <div
          className="bg-red absolute inset-0 rounded-full"
          style={{ animation: 'nectar-pulse-ring 2s ease-out infinite' }}
        />
        <div
          className="bg-red absolute inset-0 rounded-full opacity-0"
          style={{ animation: 'nectar-pulse-ring 2s ease-out infinite 0.8s' }}
        />
        <button
          type="button"
          onClick={() => onStopRecording()}
          className="bg-red relative z-1 flex size-20 items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 hover:scale-105"
        >
          <SquareIcon className="h-8" />
        </button>
      </div>
      <div className="text-body2-regular text-gray-90 flex items-center gap-3">{formatTime(seconds)}</div>
      <canvas ref={canvasRef} className="h-10 w-full" />
    </div>
  )
})
